/**
 * 신용카드 정보 관리 서비스
 * Firestore를 통한 신용카드 데이터 CRUD 작업 처리
 */

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { encrypt, decrypt } from './webCryptoService';

/**
 * 신용카드 정보 추가
 * @param {string} userId - 사용자 ID
 * @param {Object} cardData - 신용카드 정보
 * @returns {Promise<string>} 생성된 카드 ID
 */
export async function addCreditCard(userId, cardData) {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log('💳 신용카드 추가 중...');
    }

    // 민감한 정보 암호화
    const encryptedData = {
      ...cardData,
      type: 'creditcard',
      cardNumber: await encrypt(cardData.cardNumber, userId),
      cvv: await encrypt(cardData.cvv, userId),
      cardHolder: await encrypt(cardData.cardHolder, userId), // 카드 소유자명 암호화
      billingAddress: cardData.billingAddress ? 
        await encrypt(cardData.billingAddress, userId) : '', // 청구지 주소 암호화
      isFavorite: cardData.isFavorite || false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(
      collection(db, `users/${userId}/creditcards`),
      encryptedData
    );

    if (process.env.NODE_ENV === 'development') {
      console.log('✅ 신용카드 추가 완료');
    }

    return docRef.id;
  } catch (error) {
    console.error('❌ 신용카드 추가 실패:', error);
    throw new Error('신용카드 정보를 추가할 수 없습니다.');
  }
}

/**
 * 사용자의 모든 신용카드 조회
 * @param {string} userId - 사용자 ID
 * @returns {Promise<Array>} 신용카드 목록
 */
export async function getCreditCards(userId) {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log('💳 신용카드 목록 조회 중...');
    }

    const q = query(
      collection(db, `users/${userId}/creditcards`),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const cards = [];

    for (const doc of querySnapshot.docs) {
      const data = doc.data();
      
      // 민감한 정보 복호화
      const decryptedCard = {
        ...data,
        id: doc.id,
        cardNumber: await decrypt(data.cardNumber, userId),
        cvv: await decrypt(data.cvv, userId),
        cardHolder: await decrypt(data.cardHolder, userId),
        billingAddress: data.billingAddress ? 
          await decrypt(data.billingAddress, userId) : '',
        isFavorite: data.isFavorite || false,
        itemType: 'creditcard'
      };

      cards.push(decryptedCard);
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ 신용카드 ${cards.length}개 조회 완료`);
    }

    return cards;
  } catch (error) {
    console.error('❌ 신용카드 조회 실패:', error);
    throw new Error('신용카드 정보를 조회할 수 없습니다.');
  }
}

/**
 * 신용카드 정보 수정
 * @param {string} userId - 사용자 ID
 * @param {string} cardId - 카드 ID
 * @param {Object} updates - 수정할 데이터
 * @returns {Promise<void>}
 */
export async function updateCreditCard(userId, cardId, updates) {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log('💳 신용카드 수정 중...');
    }

    const updateData = {
      ...updates,
      updatedAt: serverTimestamp()
    };

    // 민감한 정보가 포함된 경우 암호화
    if (updates.cardNumber) {
      updateData.cardNumber = await encrypt(updates.cardNumber, userId);
    }
    if (updates.cvv) {
      updateData.cvv = await encrypt(updates.cvv, userId);
    }
    if (updates.cardHolder) {
      updateData.cardHolder = await encrypt(updates.cardHolder, userId);
    }
    if (updates.billingAddress) {
      updateData.billingAddress = await encrypt(updates.billingAddress, userId);
    }

    await updateDoc(
      doc(db, `users/${userId}/creditcards`, cardId),
      updateData
    );

    if (process.env.NODE_ENV === 'development') {
      console.log('✅ 신용카드 수정 완료');
    }
  } catch (error) {
    console.error('❌ 신용카드 수정 실패:', error);
    throw new Error('신용카드 정보를 수정할 수 없습니다.');
  }
}

/**
 * 신용카드 정보 삭제
 * @param {string} userId - 사용자 ID
 * @param {string} cardId - 카드 ID
 * @returns {Promise<void>}
 */
export async function deleteCreditCard(userId, cardId) {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log('💳 신용카드 삭제 중...');
    }

    await deleteDoc(doc(db, `users/${userId}/creditcards`, cardId));

    if (process.env.NODE_ENV === 'development') {
      console.log('✅ 신용카드 삭제 완료');
    }
  } catch (error) {
    console.error('❌ 신용카드 삭제 실패:', error);
    throw new Error('신용카드 정보를 삭제할 수 없습니다.');
  }
}

/**
 * 카드번호 마스킹 처리
 * @param {string} cardNumber - 카드번호
 * @returns {string} 마스킹된 카드번호
 */
export function maskCardNumber(cardNumber) {
  if (!cardNumber || cardNumber.length < 12) return cardNumber;
  
  const cleaned = cardNumber.replace(/\s/g, '');
  const firstFour = cleaned.slice(0, 4);
  const lastFour = cleaned.slice(-4);
  
  return `${firstFour} •••• •••• ${lastFour}`;
}

/**
 * 카드 타입 감지
 * @param {string} cardNumber - 카드번호
 * @returns {string} 카드 타입 (visa, mastercard, amex 등)
 */
export function detectCardType(cardNumber) {
  const cleaned = cardNumber.replace(/\s/g, '');
  
  const patterns = {
    visa: /^4/,
    mastercard: /^5[1-5]/,
    amex: /^3[47]/,
    discover: /^6(?:011|5)/,
    jcb: /^35/,
    diners: /^3(?:0[0-5]|[68])/,
    unionpay: /^62/
  };

  for (const [type, pattern] of Object.entries(patterns)) {
    if (pattern.test(cleaned)) {
      return type;
    }
  }

  return 'unknown';
}

/**
 * 카드번호 포맷팅
 * @param {string} cardNumber - 카드번호
 * @param {string} cardType - 카드 타입
 * @returns {string} 포맷된 카드번호
 */
export function formatCardNumber(cardNumber, cardType = null) {
  const cleaned = cardNumber.replace(/\s/g, '');
  
  // 카드 타입에 따른 포맷팅
  if (cardType === 'amex') {
    // Amex: 4-6-5
    return cleaned.replace(/(\d{4})(\d{6})(\d{5})/, '$1 $2 $3');
  } else {
    // 기본: 4-4-4-4
    return cleaned.replace(/(\d{4})(?=\d)/g, '$1 ');
  }
}

/**
 * 만료일 유효성 검사
 * @param {string} month - 월 (MM)
 * @param {string} year - 년도 (YY)
 * @returns {boolean} 유효 여부
 */
export function isValidExpiry(month, year) {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear() % 100;
  const currentMonth = currentDate.getMonth() + 1;

  const expMonth = parseInt(month, 10);
  const expYear = parseInt(year, 10);

  if (expMonth < 1 || expMonth > 12) return false;
  if (expYear < currentYear) return false;
  if (expYear === currentYear && expMonth < currentMonth) return false;

  return true;
}

export default {
  addCreditCard,
  getCreditCards,
  updateCreditCard,
  deleteCreditCard,
  maskCardNumber,
  detectCardType,
  formatCardNumber,
  isValidExpiry
};